import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import AllDates from "../components/AllDates";
import moment from "moment";
import FullPageLoader from "../components/FullPageLoader";
import CornerShewolf from "../images/corner/corner-shewolf.webp";
import ReservationFinder from "../components/ReservationFinder";

const CURRENT_MONTH = moment().startOf("month");

const formatDate = (date) => moment(date).format("ddd, MMM Do");
const formatMonthKey = (date) => moment(date).format("YYYY-MM");
const formatDisplayMonth = (date) => moment(date).format("MMMM YYYY");

const LiveVineyardStage = ({ info }) => {
  const [selectedArtist, setSelectedArtist] = useState(null);
  const artists = info || [];

  useEffect(() => {
    document.title = "Auburn Road - Live at the Vineyard Stage";
    return () => {
      document.title = "Auburn Road Vineyards";
    };
  }, []);

  if (!info) {
    return <FullPageLoader />;
  }

  const getAllDatesByArtist = (name) => {
    const artist = info.filter((x) => x.name === name);
    if (artist.length) {
      return artist;
    }
    return null;
  };

  const ArtistCard = ({ artist, date }) => {
    const website = artist.website;
    const { name, location } = artist;

    return (
      <article className="vineyard-performance">
        <div className="vineyard-performance__date">{formatDate(date)}</div>
        <div className="vineyard-performance__details">
          <h3 className="vineyard-performance__name m-b-0">
            {website ? (
              <a
                className="outgoing-link"
                href={website}
                target="_blank"
                rel="noopener noreferrer"
              >
                {name}
              </a>
            ) : (
              name
            )}
          </h3>
          {location && (
            <p className="vineyard-performance__location m-b-0">
              from {location}
            </p>
          )}
        </div>
      </article>
    );
  };

  const groupArtistsByMonth = (list) => {
    const grouped = list.reduce((acc, artist) => {
      if (!artist.s_date) {
        return acc;
      }
      const futureDates = artist.s_date.filter(({ date }) =>
        moment(date).isAfter(CURRENT_MONTH)
      );
      futureDates.forEach(({ date }) => {
        const monthKey = formatMonthKey(date);
        if (!acc[monthKey]) {
          acc[monthKey] = {
            displayMonth: formatDisplayMonth(date),
            artists: [],
          };
        }
        acc[monthKey].artists.push({ ...artist, date });
        acc[monthKey].artists.sort((a, b) => {
          return moment(a.date).diff(moment(b.date));
        });
      });
      return acc;
    }, {});

    return grouped;
  };

  const renderArtists = (list) => {
    const groupedByMonth = groupArtistsByMonth(list);

    return Object.entries(groupedByMonth)
      .sort(([monthA], [monthB]) => moment(monthA).diff(moment(monthB)))
      .map(([month, { displayMonth, artists: monthArtists }]) => {
        return (
          <section className="vineyard-month" key={month}>
            <h2 className="vineyard-month__title">{displayMonth}</h2>
            {monthArtists.map((artist) => (
              <ArtistCard
                key={`${artist.name}-${artist.date}`}
                artist={artist}
                date={artist.date}
              />
            ))}
          </section>
        );
      });
  };

  const groupedByMonth = groupArtistsByMonth(artists);
  const hasUpcomingShows = Object.keys(groupedByMonth).length > 0;

  return (
    <>
      <img className="corner-shewolf" src={CornerShewolf} alt="" />
      <div className="container content-page p-t-100 live-vineyard-stage">
        <header className="content-hero live-vineyard-stage__hero">
          <p className="content-hero__kicker">Music under the vines</p>
          <h1 className="huge-text-no-quote content-hero__title">LIVE!</h1>
          <p className="content-hero__intro">
            Every Saturday night we welcome performers from the region, Philly,
            New York, and around the globe to the vineyard stage.
          </p>
          <ReservationFinder className="m-t-30" />
        </header>
        <Modal
          show={selectedArtist}
          component={AllDates}
          closeFn={() => setSelectedArtist(null)}
          content={getAllDatesByArtist(selectedArtist)}
        />
        <main className="live-vineyard-stage__schedule">
          {hasUpcomingShows ? (
            renderArtists(artists)
          ) : (
            <p className="live-vineyard-stage__empty">
              Upcoming performances will be posted soon. Check back shortly for
              new dates.
            </p>
          )}
        </main>
      </div>
    </>
  );
};

export default LiveVineyardStage;
